'use client';

import { useState } from 'react';
import { ReactComponent as PhoneIcon } from '@assets/icons/phone.svg';
import { ReactComponent as UserSolid } from '@assets/icons/user-solid.svg';
import config from 'config';
import { ROUTES, ShoppingCartIcon } from 'constant';
import { usePathname, useRouter } from 'next/navigation';
import { useGetSystemPreferencesQuery } from 'services/userProfileApi';

import {
  Box,
  Button,
  Flex,
  HStack,
  Link as ChakraLink,
  Stack,
  useBreakpointValue,
  useTheme
} from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setRedirectFlow } from 'store/reducers/userSlice';

import {
  buildLoginUrlWithReturnPath,
  formatPhoneNumbers,
  formatPhoneToFormat,
  getShopDestination,
  rememberCurrentPageForPostAuthLogin,
  shouldShowHeaderShop
} from 'utils';

import { ShoppingCartName } from 'types';

import { Container } from 'components/Container';
import { Divider, Link, Text } from 'ui';
import { Cart, Logo, TabletNavigation } from './components';

const { header, shop, isFullColorLogo } = config;

const { isCommerceAvailable, shopItems } = shop;

const Icon = ShoppingCartIcon[header.shoppingCartType as ShoppingCartName];

const Header = () => {
  const router = useRouter();
  const pathname = usePathname();
  const {
    colors: {
      palette: {
        components: { headerColor }
      }
    }
  } = useTheme();
  const dispatch = useAppDispatch();
  const { data: sysPrefs, isSuccess: isSysPrefsLoaded } =
    useGetSystemPreferencesQuery('');
  const { user } = useAppSelector((state) => state.user);
  const { userInfo } = useAppSelector((state) => state.persistAuth);
  const { guestInfo } = useAppSelector((state) => state.persistGuestAuth);
  const { selectedProducts = [], selectedPackages = [] } = useAppSelector(
    (state) => state.cart
  );
  const [isShowMenu, setIsShowMenu] = useState(false);
  const isMobile = useBreakpointValue(
    { base: true, md: false },
    { fallback: 'md' }
  );

  const isLogged =
    (user !== null && Boolean(userInfo?.clid) && Boolean(userInfo?.token)) ||
    (Boolean(guestInfo?.clid) && Boolean(guestInfo?.token));
  const localItemStore = [...selectedProducts, ...selectedPackages];

  const lastameInitial = Array.from(user?.surname ?? '')[0];
  const userName = `${user?.nickname ?? user?.givname ?? ''} ${
    lastameInitial ?? ''
  }`;
  const buttonText = user ? userName : 'Sign In';
  const isNavigationAvailable = user && isMobile;
  const showDivider = !pathname.includes('dashboard');
  const isBookFlow = pathname.includes(ROUTES.booking);
  const shopDestination = (() => {
    if (isSysPrefsLoaded) {
      return getShopDestination(sysPrefs, shop);
    }
    if (shouldShowHeaderShop(undefined, shopItems, shop)) {
      return header.shopLink;
    }
    return null;
  })();
  const showShopNav = shouldShowHeaderShop(sysPrefs, shopItems, shop);
  const headerPhone =
    isSysPrefsLoaded &&
    sysPrefs?.show_phone_in_header &&
    sysPrefs?.contact_phone?.trim()
      ? sysPrefs.contact_phone.trim()
      : null;
  const formattedHeaderPhone = headerPhone
    ? formatPhoneToFormat(formatPhoneNumbers(headerPhone))
    : '';

  const handleToggle = () => setIsShowMenu((prevState) => !prevState);

  const handleBookNow = () => {
    if (!isBookFlow) {
      router.push(ROUTES.booking);
    }
  };

  const handleShopClick = () => {
    if (shopDestination) {
      router.push(shopDestination);
    }
  };

  const handleProfileClick = () => {
    if (isMobile) {
      handleToggle();
    } else {
      router.push(ROUTES.profile);
    }
  };

  const handleClick = () => {
    if (user) {
      handleProfileClick();
    } else {
      if (pathname !== ROUTES.login) {
        rememberCurrentPageForPostAuthLogin();
      }
      router.push(
        buildLoginUrlWithReturnPath(
          ROUTES.login,
          typeof window !== 'undefined'
            ? `${window.location.pathname}${window.location.search}`
            : pathname
        )
      );

      if (pathname === ROUTES.login) {
        dispatch(setRedirectFlow(null));
      }
    }
  };

  const linkStyles = {
    display: 'flex',
    justifyContent: 'center',
    fontSize: [header?.linkName ? 12 : 16, 18, 20],
    textTransform: 'uppercase',
    fontFamily: 'main',
    textAlign: 'center',
    w: { base: '50%', md: 'auto' },
    py: { base: '10px', md: 0 },
    color: headerColor.linkColor
  };

  return (
    <Box
      as='header'
      width='100vw'
      position='relative'
      left='50%'
      transform='translateX(-50%)'
      zIndex={20}
    >
      <Box
        position='relative'
        bg={headerColor.topBg}
        borderBottom='1px solid'
        borderBottomColor={headerColor.bottomBorder}
        w='100%'
      >
        <Container>
          <HStack px={{ base: '15px', md: '25px' }} py='12px' w='100%'>
            <Flex
              alignItems='center'
              justifyContent={{
                base: 'space-between',
                sm: headerPhone ? 'space-between' : 'flex-end'
              }}
              flexDirection={{
                base: headerPhone ? 'column' : 'row',
                sm: 'row'
              }}
              gap={{ base: headerPhone ? 2 : 0, sm: 8 }}
              w='100%'
            >
              {headerPhone && formattedHeaderPhone && (
                <ChakraLink
                  href={`tel:${formatPhoneNumbers(headerPhone)}`}
                  aria-label={`Call ${formattedHeaderPhone}`}
                  display='inline-flex'
                  alignItems='center'
                  gap='8px'
                  alignSelf={{ base: 'center', md: 'flex-start' }}
                  fontSize={{ base: '14px', md: '16px', lg: '18px' }}
                  fontFamily='main'
                  fontWeight={600}
                  color={headerColor.iconColor}
                  flexShrink={0}
                  whiteSpace='nowrap'
                  textDecoration='none'
                  _hover={{ textDecoration: 'none', opacity: 0.85 }}
                >
                  <PhoneIcon
                    width={18}
                    height={18}
                    fill={headerColor.iconColor}
                    aria-hidden
                  />
                  {formattedHeaderPhone}
                </ChakraLink>
              )}

              <Flex
                justifyContent='flex-end'
                alignItems='center'
                gap={45}
                w={{ base: '100%', sm: 'auto' }}
              >
                <Button
                  onClick={handleClick}
                  variant='link'
                  color={headerColor.iconColor}
                  iconSpacing='11px'
                  fontWeight={400}
                  _hover={{
                    textDecor: 'none'
                  }}
                  leftIcon={
                    <UserSolid
                      width={18}
                      height={21}
                      fill={headerColor.primaryColor}
                    />
                  }
                >
                  <Text
                    fontSize={14}
                    fontFamily='main'
                    color={headerColor.iconColor}
                    noOfLines={1}
                  >
                    {buttonText}
                  </Text>
                </Button>

                {isCommerceAvailable && header?.cartLink.includes('http') ? (
                  <Link
                    to={header?.cartLink}
                    external
                    sx={{
                      '&:hover': {
                        opacity: 0.8
                      },
                      '& > svg': {
                        width: '21px',
                        height: '21px'
                      }
                    }}
                  >
                    <Icon fill={headerColor.primaryColor} />
                  </Link>
                ) : (
                  <Cart
                    isLogged={isLogged}
                    localItemStoreLength={localItemStore.length}
                  />
                )}
              </Flex>
            </Flex>
          </HStack>
        </Container>

        <TabletNavigation
          isOpen={!!(isShowMenu && isNavigationAvailable)}
          handleToggle={handleToggle}
        />
      </Box>

      <Box position='relative' bg={headerColor.bottomBg} w='100%'>
        <Container>
          <Stack
            flexDirection={{ base: 'column', md: 'row' }}
            alignItems='center'
            pl={{
              base: 0,
              md: '25px'
            }}
            pr={{
              base: 0,
              md: '25px'
            }}
            justifyContent='space-between'
            flexWrap='wrap'
            w='full'
            gap={0}
            py={{ base: 0, md: '20px' }}
          >
            <Box
              position='relative'
              py={{ base: '17px', md: 0 }}
              display='flex'
              justifyContent={{ base: 'center', sm: 'flex-start' }}
              _after={{
                content: '""',
                position: 'absolute',
                display: { base: 'block', md: 'none' },
                width: '100vw',
                height: '1px',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                bg: headerColor.dividerColor
              }}
              sx={
                isFullColorLogo
                  ? undefined
                  : {
                      svg: {
                        fill: headerColor.logoColor
                      }
                    }
              }
            >
              <Logo />
            </Box>

            <Box
              display='flex'
              justifyContent={{
                base: 'space-between',
                md: 'flex-end'
              }}
              px={0}
              w={{ base: '100%', md: 'auto' }}
              gap={{
                base: 0,
                md: 50,
                lg: 70
              }}
              fontSize={12}
            >
              {header?.linkName && header?.linkUrl && (
                <Link
                  sx={{
                    ...linkStyles,
                    borderRight: { base: '1px solid', md: 'none' },
                    borderColor: headerColor.dividerColor,
                    '&.active-link': {
                      fontWeight: header?.linkUrl === pathname ? 600 : 400
                    }
                  }}
                  to={header?.linkUrl}
                  external={header?.linkUrl.includes('http')}
                >
                  {header?.linkName}
                </Link>
              )}
              {showShopNav && shopDestination && (
                <Button
                  onClick={handleShopClick}
                  variant='link'
                  sx={{
                    ...linkStyles,
                    fontWeight:
                      shopDestination === pathname ||
                      pathname.startsWith(`${shopDestination}/`)
                        ? 600
                        : 400
                  }}
                  _hover={{ textDecoration: 'none' }}
                >
                  Shop
                </Button>
              )}
              <Button
                onClick={handleBookNow}
                variant='link'
                sx={linkStyles}
                _hover={{ textDecoration: 'none' }}
                borderLeft={{ base: '1px solid', md: 'none' }}
                borderLeftColor={headerColor.dividerColor}
                py={{ base: '10px', md: 0 }}
                lineHeight='0px'
                borderRadius={0}
                fontWeight={isBookFlow ? 'semibold' : 'regular'}
              >
                Book Now
              </Button>
            </Box>
          </Stack>
        </Container>

        {showDivider && (
          <Divider
            position='absolute'
            bg={headerColor.dividerColor}
            opacity='0.5'
            border='none'
            height='1px'
            width='100%'
            zIndex={2}
          />
        )}
      </Box>
    </Box>
  );
};

export default Header;
